/**
 * Gaussian RBF (Radial Basis Function) interpolation for the 86-point pressure
 * sensor array → continuous 3D height surface.
 *
 * The 86 pressure points are sparse samples of a continuous pressure field.
 * To render a smooth 3D height map we fit a Gaussian RBF surface through the
 * samples and then evaluate it on a regular grid. RBF interpolation is ideal
 * here because it is mesh-free, passes through every sample exactly, and
 * produces C∞ smooth surfaces.
 *
 * Pipeline:
 *   1. `buildRBFSurface(points, sigma)`  — solves (R + λI)w = f for the weights.
 *   2. `evaluateRBFSurface(surface, x, y)` — query the surface at any point.
 *   3. `generateHeightGrid(surface, bounds, resolution)` — sample a regular grid.
 */

/** A scattered sample used to build the RBF surface. */
export interface RBFPoint {
  x: number;
  y: number;
  value: number;
}

/** A fitted RBF surface, ready to be evaluated at arbitrary points. */
export interface RBFSurface {
  points: RBFPoint[];
  weights: number[];
  sigma: number;
}

/**
 * Build an RBF surface from scattered data.
 *
 * Solves the linear system `(R + λI)w = f` where:
 *   - `R[i][j] = gaussianRBF(p_i, p_j)` is the kernel matrix,
 *   - `λ` is a small Tikhonov regularization term for numerical stability,
 *   - `f` is the vector of observed values,
 *   - `w` are the weights used to evaluate the surface.
 *
 * @param points - Scattered sample points.
 * @param sigma - Gaussian kernel bandwidth (in the same units as x/y).
 *                Larger sigma → smoother surface. Default 30.
 */
export function buildRBFSurface(points: RBFPoint[], sigma: number = 30): RBFSurface {
  const n = points.length;

  // Build RBF matrix: R[i][j] = gaussianRBF(p_i, p_j)
  const R: number[][] = Array.from({ length: n }, (_, i) =>
    Array.from(
      { length: n },
      (_, j) => gaussianRBF(points[i].x, points[i].y, points[j].x, points[j].y, sigma)
    )
  );

  // Add small Tikhonov regularization (λI) to avoid singularity
  const lambda = 0.01;
  for (let i = 0; i < n; i++) R[i][i] += lambda;

  // Solve (R + λI)w = f
  const weights = solveLinearSystem(R, points.map((p) => p.value));

  return { points, weights, sigma };
}

// ===== Fast path: precomputed inverse for fixed coordinates =====
//
// The 86 pressure point COORDINATES are fixed across frames — only the
// pressure VALUES change. We can therefore pre-compute the inverse of
// (R + λI) once (O(n³)) and reduce every subsequent per-frame solve to a
// matrix-vector product (O(n²)), a ~86× speedup for n=86.

/** A precomputed RBF inverse for a fixed set of point coordinates. */
export interface PrecomputedRBF {
  /** Fixed coordinates (no values) — one entry per sensor. */
  coords: { x: number; y: number }[];
  /** Inverse of (R + λI), where R is the Gaussian kernel matrix. */
  inverse: number[][];
  /** Gaussian kernel bandwidth used to build R. */
  sigma: number;
}

/**
 * Precompute the inverse of (R + λI) for a fixed set of coordinates.
 *
 * The coordinates are assumed to be constant across frames (e.g. the 86-point
 * pressure sensor layout). Only the `values` vector changes per frame.
 *
 * Cost: O(n³) once. Subsequent `buildRBFSurfaceFast` calls are O(n²).
 *
 * @param coords - Fixed sensor coordinates (x, y only — values supplied later).
 * @param sigma  - Gaussian kernel bandwidth. Must match the value used at
 *                 evaluation time. Default 30.
 */
export function precomputeRBFInverse(
  coords: { x: number; y: number }[],
  sigma: number = 30,
): PrecomputedRBF {
  const n = coords.length;

  // Build RBF kernel matrix R[i][j] = gaussianRBF(p_i, p_j)
  const R: number[][] = Array.from({ length: n }, (_, i) =>
    Array.from(
      { length: n },
      (_, j) =>
        gaussianRBF(coords[i].x, coords[i].y, coords[j].x, coords[j].y, sigma),
    ),
  );

  // Add Tikhonov regularization (λI) for numerical stability.
  const lambda = 0.01;
  for (let i = 0; i < n; i++) R[i][i] += lambda;

  const inverse = invertMatrix(R);
  return { coords: coords.map((c) => ({ x: c.x, y: c.y })), inverse, sigma };
}

/**
 * Build an RBF surface using a precomputed inverse — O(n²) per frame.
 *
 * `weights = inverse × values`
 *
 * The returned `RBFSurface` is structurally identical to what
 * `buildRBFSurface` produces and can be passed to `evaluateRBFSurface` /
 * `generateHeightGrid` without modification.
 *
 * @param precomputed - The precomputed inverse for this set of coordinates.
 * @param values      - Pressure values for this frame, in the same order as
 *                      `precomputed.coords`.
 */
export function buildRBFSurfaceFast(
  precomputed: PrecomputedRBF,
  values: number[],
): RBFSurface {
  const n = precomputed.coords.length;
  const weights = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    let sum = 0;
    const row = precomputed.inverse[i];
    for (let j = 0; j < n; j++) {
      sum += row[j] * values[j];
    }
    weights[i] = sum;
  }
  return {
    points: precomputed.coords.map((c, i) => ({
      x: c.x,
      y: c.y,
      value: values[i],
    })),
    weights,
    sigma: precomputed.sigma,
  };
}

/**
 * Gaussian RBF kernel: φ(r) = exp(-r² / (2σ²)).
 * Returns 1 at r=0 and decays smoothly to 0 as r → ∞.
 */
export function gaussianRBF(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  sigma: number
): number {
  const dist2 = (x1 - x2) ** 2 + (y1 - y2) ** 2;
  return Math.exp(-dist2 / (2 * sigma * sigma));
}

/**
 * Evaluate the RBF surface at a single point.
 *
 * `f(x, y) = Σ_i w_i · φ((x, y), p_i)`
 */
export function evaluateRBFSurface(surface: RBFSurface, x: number, y: number): number {
  let sum = 0;
  for (let i = 0; i < surface.points.length; i++) {
    sum +=
      surface.weights[i] *
      gaussianRBF(x, y, surface.points[i].x, surface.points[i].y, surface.sigma);
  }
  return sum;
}

/**
 * Generate a regular grid of heights for 3D rendering.
 *
 * Samples the RBF surface on a `resolution × resolution` grid covering `bounds`.
 * Heights are clamped to be non-negative (negative pressures are not physical).
 *
 * @returns Flat array of `{ x, y, height }` in row-major order.
 */
export function generateHeightGrid(
  surface: RBFSurface,
  bounds: { minX: number; maxX: number; minY: number; maxY: number },
  resolution: number = 50
): { x: number; y: number; height: number }[] {
  const grid: { x: number; y: number; height: number }[] = [];
  const stepX = (bounds.maxX - bounds.minX) / (resolution - 1);
  const stepY = (bounds.maxY - bounds.minY) / (resolution - 1);

  for (let i = 0; i < resolution; i++) {
    for (let j = 0; j < resolution; j++) {
      const x = bounds.minX + i * stepX;
      const y = bounds.minY + j * stepY;
      const height = Math.max(0, evaluateRBFSurface(surface, x, y));
      grid.push({ x, y, height });
    }
  }
  return grid;
}

/**
 * Compute the inverse of a square matrix via Gauss-Jordan elimination with
 * partial pivoting. Returns a new n×n matrix. Used internally by
 * `precomputeRBFInverse` to cache (R + λI)⁻¹ for fast per-frame solves.
 *
 * Singular rows (zero pivot encountered) produce a zero row in the inverse —
 * this matches the graceful degradation of `solveLinearSystem`.
 */
function invertMatrix(matrix: number[][]): number[][] {
  const n = matrix.length;
  // Build augmented matrix [A | I]
  const aug: number[][] = Array.from({ length: n }, (_, i) => {
    const row = matrix[i].slice();
    for (let j = 0; j < n; j++) row.push(i === j ? 1 : 0);
    return row;
  });

  // Gauss-Jordan elimination: reduce left half to identity.
  for (let i = 0; i < n; i++) {
    // Partial pivot — find the row with the largest |value| in column i.
    let maxRow = i;
    for (let k = i + 1; k < n; k++) {
      if (Math.abs(aug[k][i]) > Math.abs(aug[maxRow][i])) maxRow = k;
    }
    [aug[i], aug[maxRow]] = [aug[maxRow], aug[i]];

    const pivot = aug[i][i];
    if (Math.abs(pivot) < 1e-12) continue; // singular — leave row as-is

    // Normalize pivot row so aug[i][i] = 1.
    for (let j = i; j < 2 * n; j++) aug[i][j] /= pivot;

    // Eliminate column i from all other rows (forward + backward).
    for (let k = 0; k < n; k++) {
      if (k === i) continue;
      const factor = aug[k][i];
      if (factor === 0) continue;
      for (let j = i; j < 2 * n; j++) {
        aug[k][j] -= factor * aug[i][j];
      }
    }
  }

  // Extract the inverse from the right half of the augmented matrix.
  return aug.map((row) => row.slice(n));
}

/**
 * Solve a linear system `A x = b` using Gaussian elimination with partial
 * pivoting. Used internally by `buildRBFSurface` — not intended to be a
 * general-purpose solver.
 *
 * Singular (or near-singular) rows are handled gracefully: a zero pivot is
 * treated as producing a zero component, so the system never throws.
 */
function solveLinearSystem(matrix: number[][], vector: number[]): number[] {
  const n = vector.length;
  // Build augmented matrix [A | b]
  const aug = matrix.map((row, i) => [...row, vector[i]]);

  // Forward elimination with partial pivoting
  for (let i = 0; i < n; i++) {
    let maxRow = i;
    for (let k = i + 1; k < n; k++) {
      if (Math.abs(aug[k][i]) > Math.abs(aug[maxRow][i])) maxRow = k;
    }
    [aug[i], aug[maxRow]] = [aug[maxRow], aug[i]];

    if (Math.abs(aug[i][i]) < 1e-12) continue; // skip singular pivot

    for (let k = i + 1; k < n; k++) {
      const factor = aug[k][i] / aug[i][i];
      for (let j = i; j <= n; j++) {
        aug[k][j] -= factor * aug[i][j];
      }
    }
  }

  // Back substitution
  const solution = new Array(n).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    let sum = aug[i][n];
    for (let j = i + 1; j < n; j++) {
      sum -= aug[i][j] * solution[j];
    }
    solution[i] = Math.abs(aug[i][i]) < 1e-12 ? 0 : sum / aug[i][i];
  }
  return solution;
}
